/**
 * Breadcrumb Provider
 *
 * Centralized breadcrumb state management and hooks for the site-wide
 * breadcrumb navigation system. Uses React Router's useMatches() to
 * build breadcrumb trails from route handles.
 *
 * Task: 81.2 - Create BreadcrumbProvider
 *
 * @example
 * ```tsx
 * // In app.tsx or main.tsx
 * <BreadcrumbProvider>
 *   <RouterProvider router={router} />
 * </BreadcrumbProvider>
 *
 * // In a component
 * const breadcrumbs = useBreadcrumbs();
 *
 * // Register dynamic label
 * useRegisterBreadcrumbLabel(`/learner/workshops/${id}`, workshop?.title);
 * ```
 */

import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { useMatches, useLocation } from 'react-router-dom';
import type {
  BreadcrumbContextValue,
  BreadcrumbHandle,
  BreadcrumbItem,
} from '@/lib/breadcrumb-types';

// ──────────────────────────────────────────────────────
// Context
// ──────────────────────────────────────────────────────

const BreadcrumbCtx = createContext<BreadcrumbContextValue | null>(null);
BreadcrumbCtx.displayName = 'BreadcrumbContext';

// ──────────────────────────────────────────────────────
// Provider Component
// ──────────────────────────────────────────────────────

export interface BreadcrumbProviderProps {
  children: React.ReactNode;
}

/**
 * BreadcrumbProvider - Manages breadcrumb state and dynamic label resolution
 *
 * This provider should wrap the app at router level to access route matches.
 * It uses React Router's useMatches() to build breadcrumb trails from route handles.
 *
 * Features:
 * - Stores dynamic labels registered by components
 * - Provides context for breadcrumb resolution
 * - Memoized for performance
 */
export function BreadcrumbProvider({ children }: BreadcrumbProviderProps) {
  const [dynamicLabels, setDynamicLabelsState] = useState<Map<string, string>>(
    () => new Map()
  );

  const setDynamicLabel = useCallback((pathPattern: string, label: string) => {
    setDynamicLabelsState((prev) => {
      // Only update if the label has changed
      if (prev.get(pathPattern) === label) {
        return prev;
      }
      const next = new Map(prev);
      next.set(pathPattern, label);
      return next;
    });
  }, []);

  const contextValue = useMemo<BreadcrumbContextValue>(
    () => ({
      dynamicLabels,
      setDynamicLabel,
    }),
    [dynamicLabels, setDynamicLabel]
  );

  return (
    <BreadcrumbCtx.Provider value={contextValue}>
      {children}
    </BreadcrumbCtx.Provider>
  );
}

// ──────────────────────────────────────────────────────
// Hooks
// ──────────────────────────────────────────────────────

/**
 * useBreadcrumbContext - Access breadcrumb context for dynamic labels
 *
 * @throws Error if used outside BreadcrumbProvider
 *
 * @example
 * ```tsx
 * const { dynamicLabels, setDynamicLabel } = useBreadcrumbContext();
 * ```
 */
export function useBreadcrumbContext(): BreadcrumbContextValue {
  const context = useContext(BreadcrumbCtx);
  if (!context) {
    throw new Error(
      'useBreadcrumbContext must be used within BreadcrumbProvider'
    );
  }
  return context;
}

/**
 * useBreadcrumbs - Hook to generate breadcrumb items from current route
 *
 * Uses React Router's useMatches() to traverse the route hierarchy
 * and extract breadcrumb metadata from route handles.
 *
 * Label resolution priority:
 * 1. Dynamic labels from context (registered via useRegisterBreadcrumbLabel)
 * 2. Resolver function from route handle
 * 3. Static label from route handle
 *
 * @returns Array of BreadcrumbItem objects for rendering
 *
 * @example
 * ```tsx
 * const breadcrumbs = useBreadcrumbs();
 * // Returns: [
 * //   { label: 'Dashboard', path: '/learner/dashboard', isCurrent: false },
 * //   { label: 'Learning Hub', path: '/learner/learning-hub', isCurrent: true },
 * // ]
 * ```
 */
export function useBreadcrumbs(): BreadcrumbItem[] {
  const matches = useMatches();
  const location = useLocation();
  const { dynamicLabels } = useBreadcrumbContext();

  return useMemo(() => {
    const items: BreadcrumbItem[] = [];

    for (const match of matches) {
      const handle = match.handle as BreadcrumbHandle | undefined;
      const breadcrumb = handle?.breadcrumb;

      // Skip routes without breadcrumb config or hidden routes
      if (!breadcrumb || breadcrumb.hidden) continue;

      // Label resolution priority: dynamicLabels > resolver > static label
      const dynamicLabel = dynamicLabels.get(match.pathname);
      const resolvedLabel = breadcrumb.resolve?.(
        match.params as Record<string, string>,
        match.data
      );
      const label = dynamicLabel ?? resolvedLabel ?? breadcrumb.label;

      items.push({
        label,
        path: match.pathname,
        isCurrent: match.pathname === location.pathname,
        icon: breadcrumb.icon,
      });
    }

    // Ensure the last item is marked as current
    if (items.length > 0) {
      // Mark only the last item as current, others as not current
      items.forEach((item, index) => {
        item.isCurrent = index === items.length - 1;
      });
    }

    return items;
  }, [matches, location.pathname, dynamicLabels]);
}

/**
 * useRegisterBreadcrumbLabel - Hook for components to register dynamic labels
 *
 * Use this hook in page components that display entities with dynamic titles.
 * The registered label will be picked up by useBreadcrumbs() for rendering.
 *
 * @param pathPattern - The exact route path (e.g., '/learner/workshops/123')
 * @param label - The dynamic label to display, or undefined if not yet loaded
 *
 * @example
 * ```tsx
 * // In workshop-detail.tsx
 * const { id } = useParams<{ id: string }>();
 * const { data: workshop } = useWorkshop({ workshopId: id ?? '' });
 *
 * // Register dynamic label when workshop data is available
 * useRegisterBreadcrumbLabel(`/learner/workshops/${id}`, workshop?.title);
 * ```
 */
export function useRegisterBreadcrumbLabel(
  pathPattern: string,
  label: string | undefined
) {
  const { setDynamicLabel } = useBreadcrumbContext();

  useEffect(() => {
    if (label) {
      setDynamicLabel(pathPattern, label);
    }
  }, [pathPattern, label, setDynamicLabel]);
}
